import React, { useEffect, useState } from "react";
import { FiltersButton, TableHeader, InfoBanner, WithRoleAccess } from "@services/ui-components";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SearchComponent } from "../SearchComponent/SearchComponent";
import Button from "react-bootstrap/Button";
import { ServiceCategoriesList } from "./ServiceCategoriesList/ServiceCategoriesList";
import { CreateEditModalComponent } from "@me-pos/service-modal";
import ServiceCategoryRequestInputService from "../CreateService/utils/RequestInputService";
import i18next from "i18next";
import { useReactiveVar } from "@apollo/client";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { ImportFiles } from "../../ImportFiles/ImportFiles";
import { requirementsInfoType } from "../../../interfaces/interfaces";
import RequirementsFileInfo from "../../../services/importFiles/RequirementsFileInfo";
import { CompanyAccess, ServiceCategoryNewProxy } from "@me-team/host/main/graphql/types";
import { ExportBtn } from "../../ExportBtn/ExportBtn";
import { EntityType } from "../../../utils/enums";
import { useCreateServCategoryMutation } from "@me-team/host/main/services/graphql/services.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useParams } from "react-router-dom";
import { useRoleAccessAny } from "@me-team/host/src/utils/useRoleAccess";

export const ServiceCategories: React.FC = () => {
    const { t: translate } = useTranslation();
    const { branchId } = useParams();
    const [showFilters, setShowFilters] = useState(false);
    const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>(null);
    const [categoryList, setCategoryList] = useState<ServiceCategoryNewProxy[]>([]);
    const [reRender, setRerender] = useState<boolean>(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
    const [serviceCategoriesInfo, setServiceCategoriesInfo] = useState<requirementsInfoType>(null);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const hasCategoryHeaderActions = useRoleAccessAny([
        CompanyAccess.ServiceCategoryExport,
        CompanyAccess.ServiceCategoryImport,
        CompanyAccess.ServiceCategoryCreate,
    ]);

    useEffect(() => {
        if (currentCompanyId) {
            setServiceCategoriesInfo(
                RequirementsFileInfo.serviceCategoryFileRequirements(
                    parseInt(currentCompanyId),
                    translate,
                    i18next.language,
                    branchId
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18next.language, currentCompanyId]);
    const onOpenImportModalHandler = () => {
        setIsImportModalOpen(true);
    };
    const onCloseImportModalHandler = () => {
        setIsImportModalOpen(false);
    };
    const getCategoryList = (categoryList: ServiceCategoryNewProxy[]) => {
        setCategoryList(categoryList);
    };

    const HandleToggleFilters = () => {
        setShowFilters(!showFilters);
    };

    const searchHandling = (currentValue: string) => {
        setSearchValue(currentValue ? currentValue : null);
    };

    const onSubmitHandlerRestoreCategory = () => {
        setRerender(!reRender);
        setIsCreateCategoryModalOpen(false);
    };

    const [createCategory] = useCreateServCategoryMutation({
        fetchPolicy: "network-only",
    });

    return (
        <>
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {translate(
                        "On this page, you can edit categories and subcategories of services and create new ones."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={translate("servicesPage.serviceCategoryTitle")}
                paths={[
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${branchId}`,
                        label: translate("Services"),
                    },
                    {
                        path: `/service-categories/${branchId}`,
                        label: translate("servicesPage.serviceCategoryTitle"),
                    },
                ]}
                colSize={5}
                btnColSize={7}
                buttons={
                    <Row className="gx-3 justify-content-end mt-4 mb-2">
                        <WithRoleAccess permission={CompanyAccess.ServiceCategoryExport}>
                            <Col xs={2} xl={3} xxl={3}>
                                <ExportBtn
                                    companyId={parseInt(currentCompanyId)}
                                    exportType={EntityType.SERVICE_CATEGORIES}
                                    branchId={branchId}
                                    classes="d-none d-xl-inline-block"
                                />
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.ServiceCategoryImport}>
                            <Col xs={2} xl={3} xxl={3}>
                                <button
                                    className="btn btn-outline-violet border-primary text-primary fw-normal d-flex align-items-center justify-content-center w-100 mb-3 mb-xl-0"
                                    onClick={onOpenImportModalHandler}
                                >
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span className="d-none d-xl-inline-block ms-xl-2">{translate("import")}</span>
                                </button>
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.ServiceCategoryCreate}>
                            <Col xs={6} xl={4} xxl={4}>
                                <Button
                                    className="btn btn-primary w-100 fw-bold px-2 text-truncate"
                                    onClick={() => {
                                        setIsCreateCategoryModalOpen(true);
                                    }}
                                >
                                    <i className="bi bi-plus-lg me-2"></i> {translate("servicesPage.addCategory")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col className={`${hasCategoryHeaderActions ? "col-2" : "col-sm-5 col-xl-3"}`}>
                            <FiltersButton
                                filter={{ searchValue: searchValue }}
                                showFilters={showFilters}
                                onClick={HandleToggleFilters}
                                classesText={`${hasCategoryHeaderActions ? "d-none d-xxl-inline-block" : "d-inline-block"}`}
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <SearchComponent
                    value={searchValue}
                    placeholder={translate("searchByServiceName")}
                    searchHandling={searchHandling}
                />
            </TableHeader>
            <ServiceCategoriesList
                getCategoryList={getCategoryList}
                reRender={reRender}
                searchValue={searchValue ? searchValue.toLowerCase() : null}
                toggleShowToast={toggleShowToast}
            />
            {isCreateCategoryModalOpen && (
                <CreateEditModalComponent
                    i18n={i18next}
                    categoryId={null}
                    subcategoryId={null}
                    isOpen={isCreateCategoryModalOpen}
                    categoryInput={ServiceCategoryRequestInputService.createServiceCategoryInput(null, null, [])}
                    categoryList={categoryList}
                    isCreateAction={isCreateCategoryModalOpen}
                    onClose={() => {
                        setIsCreateCategoryModalOpen(false);
                    }}
                    onSubmit={() => {}}
                    onSubmitCreate={async (inputVariables) => {
                        currentCompanyId &&
                            (await createCategory({
                                variables: { companyId: +currentCompanyId, input: inputVariables, branchId: +branchId },
                                onCompleted: () => {
                                    onSubmitHandlerRestoreCategory();
                                    toggleShowToast(translate("Created"));
                                },
                            }));
                    }}
                    onSubmitUpdate={async () => {}}
                    requestInputService={ServiceCategoryRequestInputService}
                    currentCategory={null}
                    categoryService={null}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {serviceCategoriesInfo && (
                <ImportFiles
                    currentRequirementsInfo={serviceCategoriesInfo}
                    isOpen={isImportModalOpen}
                    onClose={onCloseImportModalHandler}
                />
            )}
        </>
    );
};
